import { computed, watch } from 'vue'
import { useAssayVuelidate } from '../assay/useAssayValidation'

/**
 * Shared control / single-cell / plate validation logic.
 * Used by GenericSample and SequencingLibrary.
 */
function useControlSingleCellPlateValidation(dv) {
  const isControl = computed(() => dv.editedRecord['is_control']?.value)
  const isSingleCell = computed(() => dv.editedRecord['is_single_cell']?.value)
  const hasPlatePosition = computed(
    () => dv.editedRecord['has_plate_position']?.value,
  )

  const controlName = computed(() => dv.editedRecord['control_name']?.value)
  const controlType = computed(() => dv.editedRecord['control_type']?.value)
  const singleCellType = computed(
    () => dv.editedRecord['single_cell_type']?.value,
  )
  const plateName = computed(() => dv.editedRecord['plate_name']?.value)
  const plateType = computed(() => dv.editedRecord['plate_type']?.value)
  const platePosition = computed(() => dv.editedRecord['plate_position']?.value)

  // Section visibility
  const showControlSection = computed(() => !!isControl.value)
  const showSingleCellSection = computed(() => !!isSingleCell.value)
  const showPlateSection = computed(() => !!hasPlatePosition.value)

  // Mandatory-if validation errors
  const controlNameError = computed(() => {
    if (!isControl.value) return null
    return controlName.value?.length > 0 ? null : 'Control name is mandatory'
  })
  const controlTypeError = computed(() => {
    if (!isControl.value) return null
    return controlType.value?.id ? null : 'Control type is mandatory'
  })
  const singleCellTypeError = computed(() => {
    if (!isSingleCell.value) return null
    return singleCellType.value?.id ? null : 'Single-cell type is mandatory'
  })
  const plateNameError = computed(() => {
    if (!hasPlatePosition.value) return null
    return plateName.value?.length > 0 ? null : 'Plate name is mandatory'
  })
  const plateTypeError = computed(() => {
    if (!hasPlatePosition.value) return null
    return plateType.value?.id ? null : 'Plate type is mandatory'
  })
  const platePositionError = computed(() => {
    if (!hasPlatePosition.value) return null
    return platePosition.value?.length > 0
      ? null
      : 'Plate position is mandatory'
  })

  // Reset fields when sections hide
  watch(showControlSection, (show) => {
    if (!show) {
      dv.resetField({ name: 'control_name' })
      dv.resetField({ name: 'control_type' })
    }
  })
  watch(showSingleCellSection, (show) => {
    if (!show) {
      dv.resetField({ name: 'single_cell_type' })
      dv.resetField({ name: 'cell_number' })
    }
  })
  watch(showPlateSection, (show) => {
    if (!show) {
      dv.resetField({ name: 'plate_name' })
      dv.resetField({ name: 'plate_type' })
      dv.resetField({ name: 'plate_code' })
      dv.resetField({ name: 'plate_position' })
    }
  })

  // Wire to vuelidate $externalResults
  const { validationErrors } = useAssayVuelidate({
    control_name: controlNameError,
    control_type: controlTypeError,
    single_cell_type: singleCellTypeError,
    plate_name: plateNameError,
    plate_type: plateTypeError,
    plate_position: platePositionError,
  })

  return {
    showControlSection,
    showSingleCellSection,
    showPlateSection,
    validationErrors,
  }
}

/**
 * Validation for GenericSample — control, single-cell, plate sections.
 * Hides sequencing-library-only single-cell fields.
 */
export function useGenericSampleValidation(dv) {
  return {
    ...useControlSingleCellPlateValidation(dv),
    hiddenFields: [
      'single_cell_library_construction',
      'single_cell_isolation',
      'single_cell_well_quality',
    ],
  }
}

/**
 * Validation for SequencingLibrary — same mandatory-if logic, no hidden fields.
 */
export function useSequencingLibraryValidation(dv) {
  return {
    ...useControlSingleCellPlateValidation(dv),
    hiddenFields: [],
  }
}

/**
 * Validation for EMSample — section visibility only, no mandatory-if rules.
 */
export function useEMSampleValidation(dv) {
  const isCryoTem = computed(() => dv.editedRecord['is_cryo_tem']?.value)
  const isArrayTomography = computed(
    () => dv.editedRecord['is_array_tomography']?.value,
  )
  const isVitrificationCryoTem = computed(
    () => dv.editedRecord['is_vitrification_cryo_tem']?.value,
  )
  const isFibmillingCryoTem = computed(
    () => dv.editedRecord['is_fibmilling_cryo_tem']?.value,
  )

  const showGridPrepCryo = computed(() => !!isCryoTem.value)
  const showGridPrepRT = computed(() => !isCryoTem.value)
  const showArrayTomography = computed(() => !!isArrayTomography.value)
  const showVitrification = computed(
    () => !!isVitrificationCryoTem.value && !!isCryoTem.value,
  )
  const showFibmilling = computed(
    () => !!isFibmillingCryoTem.value && !!isCryoTem.value,
  )

  const hiddenFields = computed(() =>
    isCryoTem.value ? [] : ['cryo_dependent_controls'],
  )

  // Reset fields when sections hide
  watch(showGridPrepCryo, (show) => {
    if (!show) {
      ;[
        'cryo_tem_grid_model',
        'instrument_type',
        'grid_prep_instrument',
        'gas_mix',
        'power',
        'glow_time',
        'micropatterned',
      ].forEach((name) => dv.resetField({ name }))
    }
  })
  watch(showGridPrepRT, (show) => {
    if (!show) {
      ;[
        'rt_tem_grid_model',
        'rt_tem_support_film',
        'rt_tem_section_thickness',
        'immunogold',
      ].forEach((name) => dv.resetField({ name }))
    }
  })
  watch(showArrayTomography, (show) => {
    if (!show) {
      ;[
        'collection_substrate',
        'sample_width',
        'amount_of_sections',
        'coating_material',
        'coating_thickness',
      ].forEach((name) => dv.resetField({ name }))
    }
  })
  watch(showVitrification, (show) => {
    if (!show) {
      ;[
        'vitrification_instrument',
        'temperature',
        'humidity',
        'blot_paper_type',
        'cryogen',
        'sample_volume',
        'fiducials_used',
        'wait_time',
        'blot_time',
        'blot_force',
        'blot_side',
      ].forEach((name) => dv.resetField({ name }))
    }
  })
  watch(showFibmilling, (show) => {
    if (!show) {
      ;[
        'fibmilling_instrument',
        'sputter_coating',
        'milling_angle',
        'sputter_coating_time',
        'number_of_lamellae',
        'gis_coating_time',
        'rough_milling_currents',
        'fine_milling_current',
        'lamella_width',
        'final_lamella_thickness',
      ].forEach((name) => dv.resetField({ name }))
    }
  })

  return {
    showGridPrepCryo,
    showGridPrepRT,
    showArrayTomography,
    showVitrification,
    showFibmilling,
    hiddenFields,
    validationErrors: {},
  }
}
